import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import { MousePointer2 } from 'lucide-react';
import type { BrushPoint, DeviceMode, StudioConfig, StudioImages } from '../types';
import { EraseCanvas } from './EraseCanvas';

interface PreviewStageProps {
  config: StudioConfig;
  images: StudioImages;
  device: DeviceMode;
}

function fontFamily(style: StudioConfig['fontStyle']) {
  if (style === 'editorial') return 'Georgia, Cambria, "Times New Roman", serif';
  if (style === 'mono') return 'Consolas, "SFMono-Regular", monospace';
  return '"Segoe UI", system-ui, sans-serif';
}

function parallaxMask(balance: number, blend: number) {
  const start = Math.max(0, Math.min(100, balance - blend / 2));
  const end = Math.max(start, Math.min(100, balance + blend / 2));
  return `linear-gradient(112deg, transparent 0%, transparent ${start}%, #000 ${end}%, #000 100%)`;
}

export function PreviewStage({ config, images, device }: PreviewStageProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const revealRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });
  const draggingRef = useRef(false);
  const [brushPoint, setBrushPoint] = useState<BrushPoint | null>(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const setCenter = () => {
      targetRef.current = { x: stage.clientWidth * 0.62, y: stage.clientHeight * 0.48 };
      currentRef.current = { ...targetRef.current };
    };
    setCenter();
    const observer = new ResizeObserver(setCenter);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [device]);

  useEffect(() => {
    let frame = 0;
    const startedAt = performance.now();
    const animate = (now: number) => {
      const stage = stageRef.current;
      const reveal = revealRef.current;
      if (stage) {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const amount = reduced ? 1 : Math.max(0.04, Math.min(0.42, config.follow / 40));
        currentRef.current.x += (targetRef.current.x - currentRef.current.x) * amount;
        currentRef.current.y += (targetRef.current.y - currentRef.current.y) * amount;
        const x = currentRef.current.x;
        const y = currentRef.current.y;
        stage.style.setProperty('--pointer-x', `${x}px`);
        stage.style.setProperty('--pointer-y', `${y}px`);
        stage.style.setProperty('--pointer-x-percent', `${(x / Math.max(1, stage.clientWidth)) * 100}%`);
        stage.style.setProperty('--pointer-y-percent', `${(y / Math.max(1, stage.clientHeight)) * 100}%`);

        if (reveal && config.mode === 'wave') {
          const elapsed = (now - startedAt) / 1000;
          const progress = x;
          const points: string[] = [];
          for (let index = 0; index <= 24; index += 1) {
            const yPoint = (index / 24) * stage.clientHeight;
            const phase = (index / 24) * Math.PI * 2 * config.waveFrequency + elapsed * (config.waveSpeed / 12);
            const xPoint = Math.max(0, Math.min(stage.clientWidth, progress + Math.sin(phase) * config.waveAmplitude));
            points.push(`${xPoint}px ${yPoint}px`);
          }
          reveal.style.clipPath = `polygon(${points.join(',')}, 100% 100%, 100% 0)`;
        }

        if (config.mode === 'parallax') {
          const nx = x / Math.max(1, stage.clientWidth) - 0.5;
          const ny = y / Math.max(1, stage.clientHeight) - 0.5;
          stage.style.setProperty('--parallax-x', `${nx * config.parallaxDepth}px`);
          stage.style.setProperty('--parallax-y', `${ny * config.parallaxDepth}px`);
        }
      }
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [config.follow, config.mode, config.parallaxDepth, config.waveAmplitude, config.waveFrequency, config.waveSpeed]);

  const updatePointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    const point = {
      x: Math.max(0, Math.min(rect.width, event.clientX - rect.left)),
      y: Math.max(0, Math.min(rect.height, event.clientY - rect.top)),
    };
    targetRef.current = point;
    if (config.mode === 'erase' && (event.pointerType === 'mouse' || draggingRef.current)) {
      setBrushPoint({ ...point, stamp: performance.now() });
    }
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    updatePointer(event);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const imageFilter = `brightness(${config.brightness}%) contrast(${config.contrast}%) saturate(${config.saturation}%) blur(${config.blur}px)`;
  const baseStyle = useMemo(() => ({
    backgroundImage: `url("${images.a}")`,
    backgroundSize: config.fit,
    backgroundPosition: `${config.imageAFocalX}% ${config.imageAFocalY}%`,
    filter: imageFilter,
    transform: config.mode === 'parallax'
      ? `translate3d(calc(var(--parallax-x) * -0.55), calc(var(--parallax-y) * -0.55), 0) scale(${config.imageAScale + config.parallaxZoom / 100})`
      : `scale(${config.imageAScale})`,
    transformOrigin: `${config.imageAFocalX}% ${config.imageAFocalY}%`,
  }), [config, imageFilter, images.a]);

  const revealStyle = useMemo(() => {
    const common: CSSProperties = {
      backgroundImage: `url("${images.b}")`,
      backgroundSize: config.fit,
      backgroundPosition: `${config.imageBFocalX}% ${config.imageBFocalY}%`,
      filter: imageFilter,
      transformOrigin: `${config.imageBFocalX}% ${config.imageBFocalY}%`,
    };
    if (config.mode === 'spotlight') {
      return {
        ...common,
        transform: `scale(${config.imageBScale})`,
        WebkitMaskImage: `radial-gradient(circle ${config.radius}px at var(--pointer-x) var(--pointer-y), #000 0, #000 ${Math.max(0, config.radius - config.feather)}px, transparent ${config.radius}px)`,
        maskImage: `radial-gradient(circle ${config.radius}px at var(--pointer-x) var(--pointer-y), #000 0, #000 ${Math.max(0, config.radius - config.feather)}px, transparent ${config.radius}px)`,
      } as CSSProperties;
    }
    if (config.mode === 'split') {
      const feather = config.splitFeather;
      return {
        ...common,
        transform: `scale(${config.imageBScale})`,
        WebkitMaskImage: feather > 0
          ? `linear-gradient(to right, transparent 0, transparent calc(var(--pointer-x) - ${feather}px), #000 calc(var(--pointer-x) + ${feather}px), #000 100%)`
          : undefined,
        maskImage: feather > 0
          ? `linear-gradient(to right, transparent 0, transparent calc(var(--pointer-x) - ${feather}px), #000 calc(var(--pointer-x) + ${feather}px), #000 100%)`
          : undefined,
        clipPath: feather === 0 ? 'polygon(var(--pointer-x) 0, 100% 0, 100% 100%, var(--pointer-x) 100%)' : undefined,
      } as CSSProperties;
    }
    if (config.mode === 'parallax') {
      const mask = parallaxMask(config.parallaxBalance, config.parallaxBlend);
      return {
        ...common,
        opacity: 1,
        filter: `${imageFilter} blur(${config.parallaxBlur}px)`,
        transform: `translate3d(var(--parallax-x), var(--parallax-y), 0) scale(${config.imageBScale + config.parallaxZoom / 100 + 0.03})`,
        WebkitMaskImage: mask,
        maskImage: mask,
      } as CSSProperties;
    }
    return { ...common, transform: `scale(${config.imageBScale})` } as CSSProperties;
  }, [config, imageFilter, images.b]);

  const stageStyle = {
    '--scene-primary': config.primary,
    '--scene-accent': config.accent,
    '--scene-background': config.background,
    '--title-size': `${config.titleSize}px`,
    '--title-font': fontFamily(config.fontStyle),
    '--vignette-opacity': `${config.vignette / 100}`,
  } as CSSProperties;

  return (
    <div className={`preview-device preview-device-${device}`}>
      <div
        ref={stageRef}
        className={`preview-stage mode-${config.mode}`}
        style={stageStyle}
        onPointerMove={updatePointer}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div className="scene-layer scene-base" style={baseStyle} />
        {config.mode !== 'erase' && <div ref={revealRef} className="scene-layer scene-reveal" style={revealStyle} />}
        {config.mode === 'erase' && <EraseCanvas source={images.b} config={config} point={brushPoint} />}
        {config.mode === 'wave' && <div className="wave-glow" aria-hidden="true" />}
        {config.mode === 'parallax' && <div className="parallax-light" aria-hidden="true" />}
        <div className="scene-vignette" aria-hidden="true" />

        <nav className="scene-nav" aria-label="生成页面预览导航">
          <strong>{config.brand}</strong>
          <div><span>Archive</span><span>Field notes</span><span>About</span></div>
          <span className="scene-nav-mark">TS</span>
        </nav>

        <main className="scene-copy">
          <span className="scene-eyebrow">{config.eyebrow}</span>
          <h1>{config.title.split('\n').map((line, index) => <span key={`${line}-${index}`}>{line}</span>)}</h1>
          <p className="scene-subtitle">{config.subtitle}</p>
          <p className="scene-description">{config.description}</p>
          <button type="button" tabIndex={-1}>{config.cta}<span>↗</span></button>
        </main>

        <div className="scene-hint" aria-hidden="true">
          <MousePointer2 size={14} />
          <span>{config.mode === 'erase' ? '移动或拖动以显影' : '移动指针以探索'}</span>
        </div>
      </div>
    </div>
  );
}
